import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { ComparisonSet } from 'frontend-utils/utils/constants/benchmark-insight';
import { capitalize } from 'frontend-utils/utils/convert-case';
import type { BeniMetaComparisonSet } from 'lumen/models';
import {
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  HIDDEN_FUTURE_COMPSET_DATA_TRANSLATIONS,
  type Kpi,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import {
  DROPDOWN_LOS_SORT_OPTIONS,
  type DropdownLosSortOption,
  type DropdownLosSortOptionType,
} from 'lumen/utils/constants/benchmark-insight/los/dropdown-los';

interface HotelBenchmarkInsightDropdownsDropdownLosDropdownLosSortSignature {
  Args: {
    changeSelectedLosSortOption: (option: DropdownLosSortOption) => void;
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    primaryComparisonSet: ComparisonSet;
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    selectedKpiLosChart: Kpi;
    selectedLosSortOption: DropdownLosSortOption;
    selectedRange: DateRangeType;
  };
}

export default class HotelBenchmarkInsightDropdownsDropdownLosDropdownLosSort extends Component<HotelBenchmarkInsightDropdownsDropdownLosDropdownLosSortSignature> {
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];

  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @cached
  get losSortOptions(): DropdownLosSortOptionType[] {
    const {
      primaryComparisonSet,
      primaryComparisonSetMeta,
      selectedRange,
      selectedKpiLosChart,
    } = this.args;
    const primaryComparisonSetIsVisible =
      primaryComparisonSetMeta?.isVisible ?? true; // As long as the API request hasn't come in, assume it's visible.
    const isHiddenFutureCompsetData =
      this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
        primaryComparisonSet,
        selectedRange.start,
        selectedKpiLosChart,
      );

    const comparisonSetTranslation = this.intl.t(
      BENI_COMPARISON_SET_LONG_TRANSLATIONS[primaryComparisonSet],
    );
    const disabledMessageTranslationKeyHiddenFutureCompsetData =
      HIDDEN_FUTURE_COMPSET_DATA_TRANSLATIONS[selectedKpiLosChart];

    let disabledMessageTranslationHiddenFutureCompsetData = '';
    if (
      isHiddenFutureCompsetData &&
      disabledMessageTranslationKeyHiddenFutureCompsetData
    ) {
      disabledMessageTranslationHiddenFutureCompsetData = this.intl.t(
        disabledMessageTranslationKeyHiddenFutureCompsetData,
      );
    }

    let disabledMessageTranslationTrialWithoutMapping = '';
    if (!this.benchmarkInsightFeatures.hasOwnPmsIntegration) {
      disabledMessageTranslationTrialWithoutMapping = this.intl.t(
        'benchmark_insight.trial_without_data.chart_legend_tooltip',
      );
    }

    const options: {
      disabled?: boolean;
      disabledMessage?: string;
      id: DropdownLosSortOption;
      label: string;
    }[] = [
      {
        id: DROPDOWN_LOS_SORT_OPTIONS.MY_HIGHEST_PERFORMING,
        label: this.intl.t(
          'benchmark_insight.los_chart.dropdown.highest_performing.own',
        ),
        disabled: !this.benchmarkInsightFeatures.hasOwnPmsIntegration,
        disabledMessage: disabledMessageTranslationTrialWithoutMapping,
      },
      {
        id: DROPDOWN_LOS_SORT_OPTIONS.PRIMARY_COMPARISON_SET_HIGHEST_PERFORMING,
        label: this.intl.t(
          'benchmark_insight.los_chart.dropdown.highest_performing.comparison_set',
          { comparisonSetType: capitalize(comparisonSetTranslation) },
        ),
        disabled: isHiddenFutureCompsetData || !primaryComparisonSetIsVisible,
        disabledMessage:
          disabledMessageTranslationHiddenFutureCompsetData ||
          disabledMessageTranslationTrialWithoutMapping,
      },
      {
        id: DROPDOWN_LOS_SORT_OPTIONS.HIGHEST_POSITIVE_DIFFERENCE_VS_PRIMARY_COMPARISON_SET,
        label: this.intl.t(
          'benchmark_insight.los_chart.dropdown.highest_positive_difference',
          {
            comparisonSetType: comparisonSetTranslation,
          },
        ),
        disabled:
          isHiddenFutureCompsetData ||
          !primaryComparisonSetIsVisible ||
          !this.benchmarkInsightFeatures.hasOwnPmsIntegration,
        disabledMessage:
          disabledMessageTranslationHiddenFutureCompsetData ||
          disabledMessageTranslationTrialWithoutMapping,
      },
      {
        id: DROPDOWN_LOS_SORT_OPTIONS.HIGHEST_NEGATIVE_DIFFERENCE_VS_PRIMARY_COMPARISON_SET,
        label: this.intl.t(
          'benchmark_insight.los_chart.dropdown.highest_negative_difference',
          {
            comparisonSetType: comparisonSetTranslation,
          },
        ),
        disabled:
          isHiddenFutureCompsetData ||
          !primaryComparisonSetIsVisible ||
          !this.benchmarkInsightFeatures.hasOwnPmsIntegration,
        disabledMessage:
          disabledMessageTranslationHiddenFutureCompsetData ||
          disabledMessageTranslationTrialWithoutMapping,
      },
    ];

    return options;
  }

  // Need to return undefined if no match is found, to match the type for `Prism::SelectInput` `value` prop.
  get selectedLosSortOption(): DropdownLosSortOptionType | undefined {
    const match = this.losSortOptions.find((option) => {
      return option.id === this.args.selectedLosSortOption;
    });
    return match ?? undefined;
  }

  @action
  changeSelectedLosSortOption(option: Option): void {
    // BENI-TODO: Remove this type casting when the platform team adds an `id` property to the `Option` type.
    const typedOption = option as DropdownLosSortOptionType;

    if (typedOption.id === this.args.selectedLosSortOption) {
      return;
    }

    this.args.changeSelectedLosSortOption(typedOption.id);

    this.usageAnalytics.track('Data Series Sorted', {
      eventTrigger: 'los',
      product: 'benchmark-insight',
      type: typedOption.id,
    });
  }

  <template>
    <PrismSelectInput
      @onChange={{this.changeSelectedLosSortOption}}
      @options={{this.losSortOptions}}
      @unsafe_isClampedToOneLine={{true}}
      @value={{this.selectedLosSortOption}}
    />
  </template>
}
